import React from 'react';
import { formatCallDuration } from './usageTiers';
import { STATUS_COLORS, STATUS_BASE_COLOR } from './UsageTimeline';
import { MILESTONE_DEFS, dayRuns, dayKey, monthTicks } from './historyEvents';

// Whole-journey timeline for one account row in the admin Accounts tab
// (History view).
//
// The one structural difference from UsageTimeline: the window arrives as
// props instead of being computed per row. Every row on screen is drawn
// against the SAME absolute axis, which is what makes the view readable
// vertically — you can run your eye down a month and compare accounts.
// HistoryAxisHeader draws the month labels once, above the table.
//
// Lanes, top to bottom:
//   1. milestones     — ordered / shipped / delivered / set up / first
//                       sign-in / first use / first call / subscription
//   2. device state   — one strip per family, one column per UTC day
//   3. calls          — sqrt-scaled bars, same shape as the Usage view
//   4. messages       — support messages up (from us) / down (from them),
//                       admin notes as faint ticks
//
// Data-dense on purpose: no axes, no legend, everything in tooltips.

const DAY_MS = 24 * 3600 * 1000;

const MILESTONE_LANE_HEIGHT = 11;
const STRIP_HEIGHT = 5;
const STRIP_GAP = 1;
const CALL_LANE_HEIGHT = 20;
const MESSAGE_LANE_HEIGHT = 9;
const LANE_GAP = 2;

const CAP_HOURS = 4;
const MIN_BAR_FRACTION = 0.15; // even a 1-minute call renders a visible tick
const CALL_BAR_WIDTH = 2;

// Minimum horizontal size for anything that must not vanish at long zooms.
const MIN_DAY_WIDTH = 0.75;
const MIN_IGLOO_DAY_WIDTH = 1.5;

// Milestone glyphs nudge apart rather than overlapping when dates cluster.
const MILESTONE_R = 3.2;
const MILESTONE_MIN_GAP = 6;

const MESSAGE_COLORS = {
  admin: '#2563eb', // us → them
  user: '#7c3aed',  // them → us
  note: '#cbd5e1',
};

const fmtDate = (ms) => new Date(ms).toLocaleDateString('en-GB', {
  day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC',
});

/** One milestone glyph. Shapes are distinguishable in monochrome too. */
function MilestoneGlyph({ shape, x, y, color, title }) {
  const r = MILESTONE_R;
  const common = { fill: color, stroke: 'white', strokeWidth: 0.6 };
  let node;
  switch (shape) {
    case 'diamond':
      node = <polygon points={`${x},${y - r} ${x + r},${y} ${x},${y + r} ${x - r},${y}`} {...common} />;
      break;
    case 'triangle':
      node = <polygon points={`${x},${y - r} ${x + r},${y + r} ${x - r},${y + r}`} {...common} />;
      break;
    case 'square':
      node = <rect x={x - r * 0.85} y={y - r * 0.85} width={r * 1.7} height={r * 1.7} {...common} />;
      break;
    case 'star':
      node = (
        <polygon
          points={starPoints(x, y, r + 0.8, r * 0.45)}
          {...common}
        />
      );
      break;
    case 'cross':
      node = (
        <g stroke={color} strokeWidth={1.6} strokeLinecap="round">
          <line x1={x - r} y1={y - r} x2={x + r} y2={y + r} />
          <line x1={x - r} y1={y + r} x2={x + r} y2={y - r} />
        </g>
      );
      break;
    default:
      node = <circle cx={x} cy={y} r={r} {...common} />;
  }
  return <g>{node}<title>{title}</title></g>;
}

function starPoints(cx, cy, outer, inner) {
  const pts = [];
  for (let i = 0; i < 10; i++) {
    const radius = i % 2 === 0 ? outer : inner;
    const angle = (Math.PI / 5) * i - Math.PI / 2;
    pts.push(`${(cx + radius * Math.cos(angle)).toFixed(2)},${(cy + radius * Math.sin(angle)).toFixed(2)}`);
  }
  return pts.join(' ');
}

/**
 * Spread clustered x positions apart, preserving order. Milestones bunch up
 * hard (shipped → delivered → set up can be one week inside a 12-month axis),
 * and an unreadable pile of glyphs is worse than a slightly displaced one.
 */
function spreadPositions(xs, minGap, maxX) {
  const out = xs.slice();
  for (let i = 1; i < out.length; i++) {
    if (out[i] - out[i - 1] < minGap) out[i] = out[i - 1] + minGap;
  }
  // If that pushed past the right edge, walk the whole run back.
  const overflow = out.length > 0 ? out[out.length - 1] - maxX : 0;
  if (overflow > 0) {
    for (let i = out.length - 1; i >= 0; i--) {
      out[i] -= overflow;
      if (i > 0 && out[i] - out[i - 1] >= minGap) break;
    }
  }
  // A dense cluster near the right edge can push the head off the left one.
  // Clamping keeps every glyph on canvas; overlap is better than invisible.
  for (let i = 0; i < out.length; i++) {
    if (out[i] < MILESTONE_R) out[i] = MILESTONE_R;
  }
  return out;
}

/**
 * One family's daily state strip.
 *
 * Drawn from RUNS of consecutive same-state days, not one rect per day: a
 * 12-month window across the fleet would otherwise emit tens of thousands of
 * SVG nodes with a tooltip each, rebuilt on every range click and re-sort. The
 * picture is identical — adjacent same-state days are the same colour — and
 * the node count becomes a function of how often devices change state rather
 * than of how wide a window is selected.
 */
const DailyStrip = React.memo(function DailyStrip({
  familyId, daily, y, width, windowStartMs, windowEndMs, loading, error,
}) {
  // Positions are derived from the window props rather than a passed-in msToX
  // closure, so every prop is a primitive or a stable object and React.memo can
  // actually do its job. The day walk below is the most expensive thing on this
  // path and it must not be redone when an unrelated part of the dashboard
  // re-renders — a sort click, a filter chip, a conversations snapshot.
  const msToX = (ms) => ((ms - windowStartMs) / (windowEndMs - windowStartMs)) * width;
  const runs = dayRuns(daily, windowStartMs, windowEndMs);

  const segments = runs.map((run) => {
    const sx = Math.max(msToX(Math.max(run.startMs, windowStartMs)), 0);
    const ex = Math.min(msToX(Math.min(run.endMs, windowEndMs)), width);
    const isIgloo = run.state === 'igloo';
    // A single igloo day must stay visible at a year's zoom.
    const w = Math.max(ex - sx, isIgloo ? MIN_IGLOO_DAY_WIDTH : MIN_DAY_WIDTH);
    return { x: Math.min(sx, width - w), w, state: run.state, isIgloo, run };
  }).filter(s => s.w > 0 && s.x < width);

  // Igloo runs last so their widened slivers aren't painted over by a
  // neighbouring long run.
  segments.sort((a, b) => Number(a.isIgloo) - Number(b.isIgloo));

  // "Still loading" is a third state, distinct from "not computed". Telling an
  // admin to press a button that starts a multi-second cloud sweep, on the
  // basis of data that was merely still arriving, is the one wrong answer here.
  // Four distinct answers, because they call for four distinct actions. Naming
  // the Update analysis button when the read itself failed would send an admin
  // to start a cloud sweep that cannot fix anything.
  const computedLabel = loading
    ? 'loading device history…'
    : error
      ? 'device history could not be read — see the note above the table'
      : daily?.days
        ? (daily.complete ? 'full history computed' : 'history still backfilling — press Update analysis again')
        : 'no history computed yet — press Update analysis';

  return (
    <g>
      <rect x={0} y={y} width={width} height={STRIP_HEIGHT} rx={1} fill={STATUS_BASE_COLOR}>
        <title>
          {`${familyId} — device state by day (grey offline, yellow asleep, orange home, green in a Higlo); ${computedLabel}`}
        </title>
      </rect>
      {segments.map((s, i) => (
        <rect
          key={i}
          x={s.x}
          y={y}
          width={s.w}
          height={STRIP_HEIGHT}
          fill={STATUS_COLORS[s.state]}
        >
          <title>{describeRun(s.run, daily)}</title>
        </rect>
      ))}
    </g>
  );
});

/** "3–9 Feb 2026 — home (6 days)" / "9 Feb 2026 — in a Higlo 42m, home 5h". */
function describeRun(run, daily) {
  const label = { igloo: 'in a Higlo', home: 'home', asleep: 'asleep', offline: 'offline', unknown: 'unknown' };
  if (run.days === 1) {
    const rec = daily?.days?.[dayKey(run.startMs)];
    return `${fmtDate(run.startMs)} — ${describeDay(rec)}`;
  }
  const last = run.endMs - DAY_MS;
  return `${fmtDate(run.startMs)} to ${fmtDate(last)} — ${label[run.state]} (${run.days} days)`;
}

/** "in a Higlo 42m, home 5h, asleep 10h" from a day record. */
function describeDay(rec) {
  if (!rec) return 'no data';
  const parts = [];
  const label = { i: 'in a Higlo', h: 'home', a: 'asleep', o: 'offline', u: 'unknown' };
  for (const key of ['i', 'h', 'a', 'o', 'u']) {
    const mins = rec[key];
    if (!mins) continue;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    parts.push(`${label[key]} ${h > 0 ? `${h}h${m > 0 ? ` ${m}m` : ''}` : `${m}m`}`);
  }
  return parts.join(', ') || 'no data';
}

export default function HistoryTimeline({
  milestones = [],       // [{ key, label, color, shape, ms, detail }]
  dailyStates = [],      // [{ familyId, days, fromMs, throughMs, complete }]
  calls = [],            // [{ startMs, durationSeconds, peakFamilies, iglooId }]
  messages = null,       // { messages: [{ms, from}], notes: [{ms, adminEmail}] } | null
  messagesUnavailable = false, // every account failed — drop the lane entirely
  messagesFailed = false,      // THIS account failed — the lane is unknown, not empty
  dailyLoading = false,        // the daily rollup read is still in flight
  dailyError = false,          // the daily rollup read failed — not "none computed"
  messagesLoading = false,     // the message fan-out is still in flight
  windowStartMs,
  windowEndMs,
  width = 620,
}) {
  const span = windowEndMs - windowStartMs;
  const msToX = (ms) => ((ms - windowStartMs) / span) * width;
  const inWindow = (ms) => ms >= windowStartMs && ms <= windowEndMs;

  const showMessages = !messagesUnavailable;
  const stripsHeight = dailyStates.length * (STRIP_HEIGHT + STRIP_GAP);

  const milestoneTop = 0;
  const stripsTop = MILESTONE_LANE_HEIGHT + LANE_GAP;
  const callTop = stripsTop + stripsHeight + LANE_GAP;
  const callBottom = callTop + CALL_LANE_HEIGHT;
  const messageTop = callBottom + LANE_GAP;
  const totalHeight = (showMessages ? messageTop + MESSAGE_LANE_HEIGHT : callBottom) + 1;

  const ticks = monthTicks(windowStartMs, windowEndMs);

  const visibleMilestones = milestones.filter(m => inWindow(m.ms));
  const milestoneXs = spreadPositions(
    visibleMilestones.map(m => Math.min(Math.max(msToX(m.ms), MILESTONE_R), width - MILESTONE_R)),
    MILESTONE_MIN_GAP,
    width - MILESTONE_R
  );

  const visibleCalls = calls.filter(c => inWindow(c.startMs));
  const visibleMessages = showMessages ? (messages?.messages || []).filter(m => inWindow(m.ms)) : [];
  const visibleNotes = showMessages ? (messages?.notes || []).filter(n => inWindow(n.ms)) : [];

  const summary = [
    `${visibleMilestones.length} milestone${visibleMilestones.length === 1 ? '' : 's'}`,
    `${visibleCalls.length} call${visibleCalls.length === 1 ? '' : 's'}`,
    showMessages ? `${visibleMessages.length} message${visibleMessages.length === 1 ? '' : 's'}` : null,
  ].filter(Boolean).join(', ');

  return (
    <svg
      width={width}
      height={totalHeight}
      className="flex-shrink-0"
      role="img"
      aria-label={`Account history from ${fmtDate(windowStartMs)} to ${fmtDate(windowEndMs)}: ${summary}`}
    >
      {/* month gridlines, spanning every lane so the eye can track vertically */}
      {ticks.map((t, i) => (
        <line
          key={i}
          x1={msToX(t.ms)}
          y1={milestoneTop}
          x2={msToX(t.ms)}
          y2={callBottom}
          stroke="#eef2f7"
          strokeWidth={1}
        />
      ))}

      {/* 1 — milestones */}
      {visibleMilestones.map((m, i) => (
        <MilestoneGlyph
          key={`${m.key}-${m.ms}`}
          shape={m.shape}
          x={milestoneXs[i]}
          y={milestoneTop + MILESTONE_LANE_HEIGHT / 2}
          color={m.color}
          title={`${m.label} — ${fmtDate(m.ms)}${m.detail ? ` (${m.detail})` : ''}`}
        />
      ))}

      {/* 2 — one device-state strip per family */}
      {dailyStates.map((fs, row) => (
        <DailyStrip
          key={fs.familyId || row}
          familyId={fs.familyId}
          daily={fs}
          y={stripsTop + row * (STRIP_HEIGHT + STRIP_GAP)}
          width={width}
          windowStartMs={windowStartMs}
          windowEndMs={windowEndMs}
          loading={dailyLoading}
          error={dailyError}
        />
      ))}

      {/* 3 — calls */}
      <line x1={0} y1={callBottom} x2={width} y2={callBottom} stroke="#e5e7eb" strokeWidth={1} />
      {visibleCalls.map((c, i) => {
        const capSeconds = CAP_HOURS * 3600;
        const frac = Math.sqrt(Math.min(c.durationSeconds, capSeconds) / capSeconds);
        const barH = Math.max(frac, MIN_BAR_FRACTION) * CALL_LANE_HEIGHT;
        const x = Math.min(Math.max(msToX(c.startMs), 0), width - CALL_BAR_WIDTH);
        const date = new Date(c.startMs);
        return (
          <rect
            key={i}
            x={x}
            y={callBottom - barH}
            width={CALL_BAR_WIDTH}
            height={barH}
            rx={0.5}
            fill="#16a34a"
          >
            <title>
              {`${date.toLocaleDateString('en-GB', { timeZone: 'UTC' })} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} — ${formatCallDuration(c.durationSeconds)}, ${c.peakFamilies} families`}
            </title>
          </rect>
        );
      })}

      {/* 4 — support messages and admin notes */}
      {showMessages && (
        <g>
          {/* An empty lane means "we never spoke to them". Neither a failed
              read nor a read still in flight may look like that, so each gets
              its own baseline: amber dashed for failed, grey dashed for
              loading, flat grey for a genuinely empty conversation. */}
          <line
            x1={0}
            y1={messageTop + MESSAGE_LANE_HEIGHT / 2}
            x2={width}
            y2={messageTop + MESSAGE_LANE_HEIGHT / 2}
            stroke={messagesFailed ? '#f59e0b' : messagesLoading ? '#cbd5e1' : '#f1f5f9'}
            strokeWidth={1}
            strokeDasharray={messagesFailed ? '3 3' : messagesLoading ? '1 3' : undefined}
          >
            {messagesFailed && <title>Message activity could not be loaded for this account — this lane is unknown, not empty.</title>}
            {!messagesFailed && messagesLoading && <title>Loading message activity…</title>}
          </line>
          {visibleNotes.map((n, i) => (
            <rect
              key={`n${i}`}
              x={Math.min(msToX(n.ms), width - 1)}
              y={messageTop + 1}
              width={1}
              height={MESSAGE_LANE_HEIGHT - 2}
              fill={MESSAGE_COLORS.note}
            >
              <title>{`Admin note — ${fmtDate(n.ms)}${n.adminEmail ? ` (${n.adminEmail})` : ''}`}</title>
            </rect>
          ))}
          {visibleMessages.map((m, i) => {
            const mid = messageTop + MESSAGE_LANE_HEIGHT / 2;
            const up = m.from === 'admin';
            return (
              <rect
                key={`m${i}`}
                x={Math.min(msToX(m.ms), width - 1.2)}
                y={up ? messageTop : mid}
                width={1.2}
                height={MESSAGE_LANE_HEIGHT / 2}
                fill={MESSAGE_COLORS[m.from]}
              >
                <title>
                  {`${up ? 'We messaged them' : 'They messaged us'} — ${fmtDate(m.ms)}`}
                </title>
              </rect>
            );
          })}
        </g>
      )}
    </svg>
  );
}

/**
 * The month scale, drawn ONCE above the table. Every row's gridlines line up
 * with these ticks, so the table reads as one chart rather than N small ones.
 */
export function HistoryAxisHeader({ windowStartMs, windowEndMs, width = 620 }) {
  const span = windowEndMs - windowStartMs;
  const msToX = (ms) => ((ms - windowStartMs) / span) * width;
  const ticks = monthTicks(windowStartMs, windowEndMs);
  const height = 16;

  return (
    <svg width={width} height={height} className="flex-shrink-0" role="presentation">
      <line x1={0} y1={height - 1} x2={width} y2={height - 1} stroke="#e5e7eb" strokeWidth={1} />
      {/* Window start, so a custom range is unambiguous even before the first
          month boundary. */}
      <text x={0} y={height - 5} fontSize={9} fill="#6b7280">
        {fmtDate(windowStartMs)}
      </text>
      {ticks.map((t, i) => {
        const x = msToX(t.ms);
        if (x > width - 18) return null;
        return (
          <g key={i}>
            <line x1={x} y1={height - 5} x2={x} y2={height - 1} stroke="#d1d5db" strokeWidth={1} />
            <text x={x + 2} y={height - 6} fontSize={9} fill="#9ca3af">{t.label}</text>
          </g>
        );
      })}
    </svg>
  );
}

/** Legend for the milestone glyphs — rendered once, next to the range control. */
export function HistoryLegend() {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-muted-foreground">
      {MILESTONE_DEFS.map(def => (
        <span key={def.key} className="flex items-center gap-1">
          <svg width={9} height={9} aria-hidden="true">
            <MilestoneGlyph shape={def.shape} x={4.5} y={4.5} color={def.color} title="" />
          </svg>
          {def.label}
        </span>
      ))}
      <span className="flex items-center gap-1">
        <span className="inline-block w-2 h-2 rounded-sm" style={{ background: MESSAGE_COLORS.admin }} />
        we messaged
      </span>
      <span className="flex items-center gap-1">
        <span className="inline-block w-2 h-2 rounded-sm" style={{ background: MESSAGE_COLORS.user }} />
        they messaged
      </span>
    </div>
  );
}
